import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function useDispatchState() {
  const { token } = useAuth();
  const [allLoads, setAllLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selections
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedLoadIds, setSelectedLoadIds] = useState(new Set());

  // Filters
  const [driverSearch, setDriverSearch] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [hasLoadsOnly, setHasLoadsOnly] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Tabs
  const [pendingTab, setPendingTab] = useState('ready');
  const [activeTab, setActiveTab] = useState('in_progress');

  // UI
  const [showSettings, setShowSettings] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [matches, setMatches] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
            const [loadsRes, driversRes, rulesRes] = await Promise.all([
        api.get('/loads'),
        api.get('/drivers'),
        api.get('/dispatch/rules'),
      ]);
      setAllLoads(loadsRes.data || []);
      setDrivers(driversRes.data || []);
      setRules(rulesRes.data || {});
    } catch (err) {
      console.error('Failed to fetch dispatch data:', err);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Driver load counts
  const driverLoadCounts = useMemo(() => {
    const counts = new Map();
    for (const load of allLoads) {
      if (!load.driver_id) continue;
      if (!counts.has(load.driver_id)) {
        counts.set(load.driver_id, { active: 0, pending: 0, completed: 0, total: 0 });
      }
      const c = counts.get(load.driver_id);
      c.total++;
      if (['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(load.status)) c.active++;
      else if (['posted', 'draft'].includes(load.status)) c.pending++;
      else if (['completed', 'invoiced'].includes(load.status)) c.completed++;
    }
    return counts;
  }, [allLoads]);

  // Filtered drivers
  const filteredDrivers = useMemo(() => {
    let list = drivers;
    if (driverSearch) {
      const q = driverSearch.toLowerCase();
      list = list.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.email || '').toLowerCase().includes(q) ||
        (d.phone || '').toLowerCase().includes(q)
      );
    }
    if (driverFilter === 'active') {
      list = list.filter(d => {
        const c = driverLoadCounts.get(d.user_id);
        return c && c.active > 0;
      });
    } else if (driverFilter === 'idle') {
      list = list.filter(d => {
        const c = driverLoadCounts.get(d.user_id);
        return !c || c.active === 0;
      });
    }
    if (hasLoadsOnly) {
      list = list.filter(d => driverLoadCounts.has(d.user_id));
    }
    return list;
  }, [drivers, driverSearch, driverFilter, hasLoadsOnly, driverLoadCounts]);

  // Selected driver object
  const selectedDriver = useMemo(
    () => drivers.find(d => d.user_id === selectedDriverId) || null,
    [drivers, selectedDriverId]
  );

  // Helper to search loads
  const searchLoads = (loads, query) => {
    if (!query) return loads;
    const q = query.toLowerCase();
    return loads.filter(l =>
      (l.load_id || '').toLowerCase().includes(q) ||
      (l.cargo_description || '').toLowerCase().includes(q) ||
      (l.pickup_name || '').toLowerCase().includes(q) ||
      (l.delivery_name || '').toLowerCase().includes(q) ||
      (l.pickup_address || '').toLowerCase().includes(q) ||
      (l.delivery_address || '').toLowerCase().includes(q)
    );
  };

  // Pending loads (top grid)
  const pendingLoads = useMemo(() => {
    let loads;
    switch (pendingTab) {
      case 'ready':
        loads = allLoads.filter(l => l.status === 'posted' || l.status === 'draft');
        break;
      case 'quotes':
        loads = allLoads.filter(l => l.status === 'draft');
        break;
      case 'scheduled':
        loads = allLoads.filter(l => l.status === 'assigned');
        break;
      case 'cancelled':
        loads = allLoads.filter(l => l.status === 'cancelled');
        break;
      default:
        loads = [];
    }
    return searchLoads(loads, pendingSearch);
  }, [allLoads, pendingTab, pendingSearch]);

  // Active loads (bottom grid)
  const activeLoads = useMemo(() => {
    let loads;
    switch (activeTab) {
      case 'in_progress':
        loads = allLoads.filter(l =>
          ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status)
        );
        break;
      case 'delivered':
        loads = allLoads.filter(l =>
          ['completed', 'invoiced'].includes(l.status)
        );
        break;
      default:
        loads = [];
    }
    return searchLoads(loads, activeSearch);
  }, [allLoads, activeTab, activeSearch]);

  // Selection helpers
  const toggleLoadSelection = useCallback((loadId) => {
    setSelectedLoadIds(prev => {
      const next = new Set(prev);
      if (next.has(loadId)) next.delete(loadId);
      else next.add(loadId);
      return next;
    });
  }, []);

  const selectAllLoads = useCallback((loadIds) => {
    setSelectedLoadIds(new Set(loadIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLoadIds(new Set());
  }, []);

  // Actions
  const assignLoads = useCallback(async () => {
    if (!selectedDriverId || selectedLoadIds.size === 0) {
      toast.error('Select a driver and at least one load');
      return;
    }
        let success = 0;
    for (const loadId of selectedLoadIds) {
      try {
        await api.put(`/loads/${loadId}/assign`, { driver_id: selectedDriverId });
        success++;
      } catch (err) {
        toast.error(`Failed to assign ${loadId}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (success > 0) {
      toast.success(`Assigned ${success} load(s) to driver`);
      clearSelection();
      fetchData();
    }
  }, [token, selectedDriverId, selectedLoadIds, clearSelection, fetchData]);

  const dispatchLoads = useCallback(async () => {
    if (!selectedDriverId || selectedLoadIds.size === 0) {
      toast.error('Select a driver and at least one load');
      return;
    }
        let success = 0;
    for (const loadId of selectedLoadIds) {
      try {
        await api.put(`/loads/${loadId}/assign`, { driver_id: selectedDriverId });
        await api.put(`/loads/${loadId}/status`, { status: 'at_pickup' });
        success++;
      } catch (err) {
        toast.error(`Failed to dispatch ${loadId}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (success > 0) {
      toast.success(`Dispatched ${success} load(s)`);
      clearSelection();
      fetchData();
    }
  }, [token, selectedDriverId, selectedLoadIds, clearSelection, fetchData]);

  const cancelLoads = useCallback(async () => {
    if (selectedLoadIds.size === 0) return;
        let success = 0;
    for (const loadId of selectedLoadIds) {
      try {
        await api.put(`/loads/${loadId}/status`, { status: 'cancelled' });
        success++;
      } catch (err) {
        toast.error(`Failed to cancel ${loadId}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (success > 0) {
      toast.success(`Cancelled ${success} load(s)`);
      clearSelection();
      fetchData();
    }
  }, [token, selectedLoadIds, clearSelection, fetchData]);

  const undeliverLoads = useCallback(async () => {
    if (selectedLoadIds.size === 0) return;
        let success = 0;
    for (const loadId of selectedLoadIds) {
      try {
        await api.put(`/loads/${loadId}/status`, { status: 'at_delivery' });
        success++;
      } catch (err) {
        toast.error(`Failed to undeliver ${loadId}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (success > 0) {
      toast.success(`Reverted ${success} load(s) to at_delivery`);
      clearSelection();
      fetchData();
    }
  }, [token, selectedLoadIds, clearSelection, fetchData]);

  const handleScore = useCallback(async (loadId) => {
    setScoring(true);
    try {
      const res = await api.post(`/matching/score/${loadId}`);
      setMatches(res.data.matches || []);
      toast.success(`Scored ${res.data.total_drivers_scored} drivers`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to score');
    } finally {
      setScoring(false);
    }
  }, [token]);

  const handleAutoDispatch = useCallback(async (loadId) => {
    try {
      const res = await api.post(`/matching/auto-dispatch/${loadId}`);
      toast.success(`Auto-dispatched: ${res.data.offers_sent} offers sent`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to auto-dispatch');
    }
  }, [token, fetchData]);

  const handleOffer = useCallback(async (matchId) => {
    try {
      await api.post(`/matching/offer/${matchId}`);
      toast.success('Offer sent to driver');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  }, [token]);

  const handleSaveRules = useCallback(async () => {
    try {
      await api.put('/dispatch/rules', rules);
      toast.success('Dispatch rules saved');
      setShowSettings(false);
    } catch (err) {
      toast.error('Failed to save rules');
    }
  }, [token, rules]);

  return {
    // Data
    allLoads, drivers, rules, setRules, loading,
    pendingLoads, activeLoads,
    filteredDrivers, driverLoadCounts,
    selectedDriver, selectedDriverId, setSelectedDriverId,
    selectedLoadIds, toggleLoadSelection, selectAllLoads, clearSelection,

    // Filters
    driverSearch, setDriverSearch,
    driverFilter, setDriverFilter,
    hasLoadsOnly, setHasLoadsOnly,
    pendingSearch, setPendingSearch,
    activeSearch, setActiveSearch,

    // Tabs
    pendingTab, setPendingTab,
    activeTab, setActiveTab,

    // UI
    showSettings, setShowSettings,
    scoring, matches,

    // Actions
    fetchData, assignLoads, dispatchLoads, cancelLoads, undeliverLoads,
    handleScore, handleAutoDispatch, handleOffer, handleSaveRules,
  };
}
